import React from "react";
import SEOHead from "@/components/SEOHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Brain,
  BarChart3,
  Cpu,
  LineChart,
  Sparkles,
  ShieldCheck,
  Target,
  Zap,
} from "lucide-react";

const SobreNosPage: React.FC = () => {
  const tecnologias = [
    {
      icon: <Brain className="h-6 w-6" />,
      title: "Inteligência Artificial Aplicada",
      desc: "Modelos de IA treinados para identificar padrões em grandes volumes de resultados históricos.",
    },
    {
      icon: <BarChart3 className="h-6 w-6" />,
      title: "Estatísticas Históricas",
      desc: "Frequência de dezenas, centenas, milhares e grupos analisada em janelas temporais configuráveis.",
    },
    {
      icon: <Cpu className="h-6 w-6" />,
      title: "Machine Learning",
      desc: "Algoritmos que aprendem com séries históricas para sugerir combinações estratégicas.",
    },
    {
      icon: <LineChart className="h-6 w-6" />,
      title: "Tendências Matemáticas",
      desc: "Cruzamento de séries, periodicidade e indicadores de variação para destacar padrões relevantes.",
    },
    {
      icon: <Sparkles className="h-6 w-6" />,
      title: "Simulações Estratégicas",
      desc: "Geração de cenários e combinações com base em desdobramentos e métodos consagrados.",
    },
  ];

  return (
    <>
      <SEOHead
        title="Sobre Nós — IA Palpite Guru"
        description="Conheça o IA Palpite Guru: plataforma de análises estatísticas e inteligência artificial aplicada para o Jogo do Bicho e Loterias da Caixa, com foco em uso responsável e entretenimento."
        keywords="sobre ia palpite guru, plataforma jogo do bicho, inteligência artificial loterias, análises estatísticas bicho, tecnologia ia palpites"
        canonical="/sobre-nos"
      />
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 py-12 max-w-5xl">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-purple-600 text-white mb-6 shadow-lg shadow-primary/30">
              <Brain className="h-8 w-8" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary via-purple-500 to-amber-400 bg-clip-text text-transparent">
              Sobre o IA Palpite Guru
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Uma plataforma moderna que une <strong className="text-foreground">inteligência artificial</strong>,
              <strong className="text-foreground"> estatísticas históricas</strong> e
              <strong className="text-foreground"> simulações estratégicas</strong> para apoiar entusiastas do
              Jogo do Bicho e das Loterias da Caixa.
            </p>
          </div>

          {/* História */}
          <Card className="mb-8 bg-card/60 backdrop-blur border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Target className="h-6 w-6 text-primary" />
                Nossa História
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                O <strong className="text-foreground">IA Palpite Guru</strong> nasceu da combinação entre a paixão pelo
                Jogo do Bicho e o avanço da inteligência artificial. Notamos que apostadores tradicionais dependiam
                apenas de intuição, enquanto uma enorme quantidade de dados históricos permanecia sem análise.
              </p>
              <p>
                Reunimos modelos de IA, ciência de dados e métodos clássicos numa única plataforma com foco em
                <strong className="text-foreground"> apoio estratégico</strong> — não em promessas. Toda análise é
                apresentada como insumo para decisões conscientes do usuário.
              </p>
            </CardContent>
          </Card>

          {/* Tecnologias */}
          <Card className="mb-8 bg-card/60 backdrop-blur border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Cpu className="h-6 w-6 text-primary" />
                Tecnologias Utilizadas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {tecnologias.map((t) => (
                  <div
                    key={t.title}
                    className="flex gap-4 p-4 rounded-xl bg-background/40 border border-border hover:border-primary/40 transition-colors"
                  >
                    <div className="shrink-0 w-11 h-11 rounded-lg bg-gradient-to-br from-primary/20 to-amber-400/20 flex items-center justify-center text-primary">
                      {t.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">{t.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{t.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Como funcionam as ferramentas */}
          <Card className="mb-8 bg-card/60 backdrop-blur border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Zap className="h-6 w-6 text-primary" />
                Como Funcionam Nossas Ferramentas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-muted-foreground leading-relaxed">
              <p>
                Cada ferramenta cruza resultados oficiais coletados automaticamente com algoritmos próprios para
                destacar dezenas, centenas, milhares e grupos com maior frequência no período selecionado.
              </p>
              <p>
                Os métodos clássicos (Cruz do Dia, 7 Grupos, Chave de Ouro, entre outros) foram digitalizados e
                integrados a uma camada de IA que executa <strong className="text-foreground">simulações estratégicas</strong>
                em segundos.
              </p>
            </CardContent>
          </Card>

          {/* Aviso central */}
          <Card className="mb-8 border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-primary/5">
            <CardContent className="p-6 flex items-start gap-4">
              <ShieldCheck className="h-8 w-8 text-amber-400 shrink-0" />
              <div>
                <h3 className="font-bold text-foreground mb-1">Compromisso com a transparência</h3>
                <p className="text-muted-foreground leading-relaxed">
                  O <strong className="text-foreground">IA Palpite Guru não realiza previsões garantidas nem promete
                  premiações</strong>. Todo o conteúdo é fornecido como apoio estatístico e estratégico, para fins de
                  entretenimento e uso consciente.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* CTA suave */}
          <div className="text-center">
            <Link to="/planos">
              <Button size="lg" className="bg-gradient-to-r from-primary to-purple-600 hover:opacity-90">
                Conhecer nossos planos
              </Button>
            </Link>
            <p className="mt-3 text-xs text-muted-foreground">
              Veja também a página de{" "}
              <Link to="/uso-responsavel" className="underline hover:text-foreground">
                Uso Responsável
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SobreNosPage;
